// app/shared/components/marketing/CustomerStoryCardComponent.tsx
"use client";

import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { CustomerStory } from "@/app/features/customers/customers.interface";
import {
  accentBgClasses,
  accentHoverBgClasses,
} from "@/app/features/customers/utils/accentColors";
import { storyInitials } from "@/app/features/customers/utils/storyInitials";
import { cn } from "@/lib/utils";

interface CustomerStoryCardProps {
  story: CustomerStory;
  /** 'default' = text left, image right | 'inverted' = image left, text right */
  variant?: "default" | "inverted";
  /** 'lg' for /customers page, 'md' for homepage */
  size?: "lg" | "md";
}

export function CustomerStoryCardComponent({
  story,
  variant = "default",
  size = "lg",
}: CustomerStoryCardProps) {
  const isInverted = variant === "inverted";
  const isLarge = size === "lg";

  // Size-based styles
  const containerPadding = isLarge ? "p-10 md:p-12" : "p-8 md:p-10";
  const imagePadding = isLarge ? "p-4" : "p-4";
  const imageMinHeight = isLarge ? "min-h-[400px]" : "min-h-[300px]";
  const imageInnerMinHeight = isLarge ? "380px" : "280px";
  const titleSize = isLarge ? "text-3xl md:text-4xl" : "text-2xl md:text-3xl";
  const descriptionSize = isLarge ? "text-xl" : "text-lg";
  const metricSize = isLarge ? "text-5xl md:text-6xl" : "text-4xl md:text-5xl";
  const metricLabelSize = isLarge ? "text-lg" : "text-sm";
  const arrowSize = isLarge ? 26 : 22;
  const arrowBoxSize = isLarge ? "w-14 h-14" : "w-12 h-12";
  const borderPadding = isLarge ? "pt-6" : "pt-4";
  const titleMargin = isLarge ? "mb-5" : "mb-4";
  const descriptionMargin = isLarge ? "mb-6" : "mb-4";

  // Content section
  const ContentSection = (
    <div
      className={cn(
        containerPadding,
        "flex flex-col justify-between bg-surface-light",
      )}
    >
      <div>
        {!isLarge && (
          <span className="text-sm text-gray-500 mb-2 block">
            {story.industry}
          </span>
        )}
        <h2 className={cn(titleSize, "font-bold text-black", titleMargin)}>
          {story.customerName}
        </h2>
        <p
          className={cn(
            descriptionSize,
            "text-gray-700 leading-relaxed",
            descriptionMargin,
          )}
        >
          {story.description}
        </p>
      </div>

      {/* Bottom metrics row */}
      <div
        className={cn(
          "flex items-end justify-between border-t border-dashed border-gray-300",
          borderPadding,
        )}
      >
        <div>
          {story.heroMetrics && story.heroMetrics[0] && (
            <>
              <span
                className={cn(
                  metricSize,
                  "font-bold text-black block leading-none",
                )}
              >
                {story.heroMetrics[0].value}
              </span>
              <p className={cn(metricLabelSize, "text-gray-500 mt-1")}>
                {story.heroMetrics[0].label}
              </p>
            </>
          )}
        </div>
        <div
          className={cn(
            arrowBoxSize,
            "rounded-xl border border-gray-300 flex items-center justify-center transition-all duration-300",
            accentHoverBgClasses[story.accentColor],
            "group-hover:border-transparent",
          )}
        >
          <ArrowUpRight
            size={arrowSize}
            className="text-gray-500 group-hover:text-white transition-colors"
          />
        </div>
      </div>
    </div>
  );

  // Image section
  const ImageSection = (
    <div
      className={cn(
        imagePadding,
        "flex items-center justify-center",
        imageMinHeight,
        accentBgClasses[story.accentColor],
      )}
    >
      {story.logoUrl ? (
        <div
          className="rounded-xl overflow-hidden w-full h-full relative"
          style={{ minHeight: imageInnerMinHeight }}
        >
          <Image
            src={story.logoUrl}
            alt={story.customerName}
            fill
            className="object-cover"
          />
        </div>
      ) : (
        <span
          className="font-bold text-white/20"
          style={{
            fontSize: isLarge
              ? "clamp(6rem, 15vw, 10rem)"
              : "clamp(5rem, 12vw, 8rem)",
          }}
        >
          {storyInitials(story.customerName)}
        </span>
      )}
    </div>
  );

  return (
    <Link
      href={`/customers/${story.slug}`}
      className="group block rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
        {isInverted ? (
          <>
            {ImageSection}
            {ContentSection}
          </>
        ) : (
          <>
            {ContentSection}
            {ImageSection}
          </>
        )}
      </div>
    </Link>
  );
}
